<template>
    <div>
        <g-typography tag="p" font-weight="bold" class="margin-bottom-small">
            There are 3-steps to delivering your content.
        </g-typography>

        <g-typography tag="p" size="small" class="margin-bottom-normal">
            For help, visit our
            <a :href="FAQ_UPLOADED_CONTENT_DELIVERY" target="_blank">help documentation</a>.
        </g-typography>

        <error-message-card v-if="errorMessage && !errors.url" :message="errorMessage" />

        <g-form>
            <div class="margin-bottom-small">
                <g-loader v-if="isFileDeleting || isFileUploading" type="absolute" />
                <g-typography
                    tag="p"
                    font-weight="semibold"
                    value="1. Upload your media file"
                    class="margin-bottom-small"
                />

                <div v-for="(file, i) in files.current" :key="i" class="margin-bottom-normal">
                    <activation-file :file="file" :deletable="true" @file-deleted="handleFileDeleted" />
                </div>

                <g-upload
                    :headers="uploadHeaders"
                    :action="uploadContentUrl"
                    :multiple="true"
                    :show-upload-list="false"
                    :before-upload="handleBeforeUpload"
                    @change="handleFileChange"
                >
                    <g-button
                        class="full-width-mobile"
                        shape="round"
                        theme="live"
                        size="large"
                        :disabled="isLoading || isSubmitting || isFileUploading || isFileDeleting"
                        :loading="isFileUploading"
                    >
                        <g-icon v-if="!isFileUploading" name="upload" />
                        Upload Content
                    </g-button>
                </g-upload>

                <g-typography tag="p" size="small" color="grey-6" class="margin-bottom-normal">
                    <span v-if="isTiktok"> Files accepted: mp4, webm. Up to 500mb per file. </span>
                    <span v-else-if="isPinterest">
                        Files accepted: jpg, jpeg, png, mov, mp4, m4v. Up to 500mb per file.
                    </span>
                    <span v-else>Files accepted: jpg, jpeg, png, bmp, gif, mov, mp4. Up to 500mb per file.</span>
                </g-typography>
            </div>

            <g-typography
                tag="p"
                font-weight="semibold"
                value="2. Paste the link to your content"
                class="margin-bottom-small"
            />

            <g-form-item :errors="errors.url" label="Paste the link to your content" class="margin-bottom-normal">
                <g-input
                    v-model="url"
                    placeholder=" Paste the link to your content here"
                    size="large"
                    :disabled="isSubmitting"
                />
            </g-form-item>

            <g-form-item v-if="dateEnabled" label="Date of Post">
                <g-date-picker v-model="postedAt" class="date-picker" size="large" :disabled="isSubmitting" />
            </g-form-item>

            <template v-if="isContentManualStatsEnabled">
                <g-typography
                    tag="p"
                    value="Note: Content metrics can be updated after delivery under Delivered content below."
                    class="margin-bottom-small"
                />
            </template>
            <template v-else>
                <g-typography
                    tag="p"
                    font-weight="semibold"
                    value="3. Enter your content metrics"
                    class="margin-bottom-small"
                />

                <g-form-item
                    v-if="!isTiktok && !isPinterest"
                    :errors="errors.impressions"
                    label="Impressions"
                    class="margin-bottom-small"
                >
                    <g-input v-model="metrics.impressions" size="large" :disabled="isSubmitting" />
                </g-form-item>

                <g-form-item :errors="errors.likes" :label="isPinterest ? 'Saves' : 'Likes'" class="margin-bottom-small">
                    <g-input v-model="metrics.likes" size="large" :disabled="isSubmitting" />
                </g-form-item>

                <g-form-item v-if="!isPinterest" :errors="errors.comments" label="Comments" class="margin-bottom-small">
                    <g-input v-model="metrics.comments" size="large" :disabled="isSubmitting" />
                </g-form-item>

                <g-form-item
                    v-if="isTiktok || isPinterest || hasVideoFile"
                    :errors="errors.views"
                    :label="isPinterest ? 'Impressions' : 'Views'"
                    class="margin-bottom-small"
                >
                    <g-input v-model="views" size="large" :disabled="isSubmitting" />
                </g-form-item>
            </template>
        </g-form>
    </div>
</template>

<script>
    import { ref, reactive, toRefs, computed, watch } from 'vue-demi';
    import { message } from '@grininc/grin-ui';
    import { FAQ_UPLOADED_CONTENT_DELIVERY } from '../../../../../constants/faqs';
    import { NETWORK_INFO_BY_ID } from '../../../../../constants/networks';
    import {
        EVENT_NAME_MANUAL_DELIVERY_UPLOAD_CONTENT_CLICKED,
        EVENT_NAME_MANUAL_DELIVERY_UPLOAD_TOO_LARGE,
        EVENT_NAME_MANUAL_DELIVERY_DELIVER_CLICKED
    } from '../../../../../constants/analytics-events';
    import ErrorMessageCard from '../../../proposals/partials/ErrorMessageCard.vue';
    import ActivationFile from '../../../../library/ActivationFile.vue';
    import { getMediaLibraryFilePaths } from '../../../../../helpers/activations/activation-files';
    import useHttp from '../../../../../../src/composables/use-http';
    import useNetworks from '../../../../../../src/composables/use-networks';
    import useEventTracking from '../../../../../../src/composables/use-event-tracking';
    import useActivationBriefs from '../../../../../../src/composables/use-activation-briefs';
    import useContactStore from '../../../../../../stores/contact';
    import useFeatureFlagsStore from '../../../../../../stores/feature-flags';

    export default {
        name: 'TaskDeliverUploadedContent',

        components: {
            ErrorMessageCard,
            ActivationFile
        },
        props: {
            isLoading: {
                type: Boolean,
                default: false
            },
            isDelivering: {
                type: Boolean,
                default: false
            },
            deliverUploadedContent: {
                type: Boolean,
                default: false
            },
            errorMessage: {
                type: String
            },
            errors: {
                type: [Array, Object]
            },
            networkId: {
                type: Number,
                default: null
            },
            uploadContentUrl: {
                type: String,
                default: ''
            },
            uploadedContentDeliveryUrl: {
                type: String,
                default: ''
            },
            deleteUploadedContentUrl: {
                type: String,
                default: ''
            },
            uploadHeaders: {
                type: Object,
                default: null
            },
            hasUploadedFiles: {
                type: Boolean,
                default: false
            },
            uploadedFiles: {
                type: Array,
                default: null
            },
            isFileUploading: {
                type: Boolean,
                default: false
            }
        },

        setup(props, { emit, root }) {
            const { http } = useHttp();
            const { trackEvent } = useEventTracking();
            const {
                networkId,
                uploadedFiles,
                deliverUploadedContent,
                uploadedContentDeliveryUrl,
                deleteUploadedContentUrl
            } = toRefs(props);
            const isSubmitting = ref(false);
            const isFileDeleting = ref(false);
            const dateEnabled = ref(false);
            const url = ref(null);
            const postedAt = ref(null);
            const views = ref(0);
            const contact = useContactStore().data;
            const { getActivationBriefs } = useActivationBriefs();
            const featureFlags = useFeatureFlagsStore().data;

            const initialFileState = {
                current: [],
                deleted: []
            };

            const initalMetricState = {
                impressions: 0,
                likes: 0,
                comments: 0
            };

            const files = reactive({ ...initialFileState });
            const metrics = reactive({ ...initalMetricState });

            const { isMetaById, isTiktokById, isPinterestById } = useNetworks();
            const isMeta = computed(() => isMetaById(networkId.value));
            const isTiktok = computed(() => isTiktokById(networkId.value));
            const isPinterest = computed(() => isPinterestById(networkId.value));

            const isContentManualStatsEnabled = computed(() => (
                isMeta.value && featureFlags['content-manual-stats-enabled'] || false)
            );

            dateEnabled.value = false;

            const filterDeletedFiles = (filesToFilter) => {
                return filesToFilter?.filter((file) => !files.deleted.includes(file.name)) || [];
            };

            const getCurrentFiles = () => {
                const validCurrentFiles = filterDeletedFiles(uploadedFiles.value);
                return getMediaLibraryFilePaths(validCurrentFiles);
            };

            const hasVideoFile = computed(() => {
                return files.current?.some((file) => /video\//.test(file.mime_type));
            });

            const updateFileList = () => {
                files.current = getCurrentFiles();
            };

            const resetForm = () => {
                url.value = null;
                views.value = 0;
                Object.assign(metrics, initalMetricState);
                Object.assign(files, initialFileState);
            };

            const handleBeforeUpload = (file) => {
                trackEvent(EVENT_NAME_MANUAL_DELIVERY_UPLOAD_CONTENT_CLICKED, {
                    network: NETWORK_INFO_BY_ID[networkId.value].slug
                });

                const isLt500MB = file.size / 1000000 < 500;
                if (!isLt500MB) {
                    message.error('File must be smaller than 500MB.');
                    handleUploadFailed();
                    trackEvent(EVENT_NAME_MANUAL_DELIVERY_UPLOAD_TOO_LARGE, {
                        network: NETWORK_INFO_BY_ID[networkId.value].slug
                    });
                }
                return isLt500MB;
            };

            const handleFileChange = (info) => {
                emit('file-change', info);
                updateFileList();
            };

            const handleUploadFailed = () => {
                emit('upload-failed');
            };

            const handleResetUploads = () => {
                emit('reset-uploads');
            };

            const isValidMetric = (metric) => {
                return metric || metric === 0;
            };

            const cleanMetric = (metric) => {
                const num = ('' + metric).replace(/[^0-9]/gi, '');
                return num || num === 0 ? parseInt(num) : '';
            };

            const cleanMetrics = () => {
                for (const key in metrics) {
                    metrics[key] = cleanMetric(metrics[key]);
                }

                views.value = cleanMetric(views.value);
            };

            const isReady = computed(() => {
                cleanMetrics();
                const hasFiles = files?.current?.length > 0;

                if (
                    !hasFiles ||
                    !url.value ||
                    (dateEnabled.value && !postedAt.value) ||
                    !isValidMetric(metrics.impressions) ||
                    !isValidMetric(metrics.likes) ||
                    !isValidMetric(metrics.comments) ||
                    (hasVideoFile.value && !isValidMetric(views.value))
                ) {
                    return false;
                }

                return true;
            });

            const getPayload = () => {
                const payload = {
                    files: files?.current,
                    url: url.value,
                    posted_at: postedAt.value,
                    metrics: Object.assign({}, metrics)
                };

                if (hasVideoFile.value || isTiktok.value || isPinterest.value) {
                    payload.metrics.views = views.value;
                }

                return payload;
            };

            const handleSubmit = async () => {
                trackEvent(EVENT_NAME_MANUAL_DELIVERY_DELIVER_CLICKED, {
                    network: NETWORK_INFO_BY_ID[networkId.value].slug
                });

                const payload = getPayload();

                if (!isReady.value || !payload) {
                    return;
                }

                isSubmitting.value = true;

                try {
                    await http.post(uploadedContentDeliveryUrl.value, payload);

                    if (featureFlags['task-delivery-improvements-enabled']) {
                        const res = await getActivationBriefs(contact.id);
                        root.briefs = res.data;
                    }

                    message.success('Your content has been delivered.');
                    resetForm();
                    emit('reset-uploads');
                    emit('deliver-uploaded-content');
                    emit('exit');
                } catch (e) {
                    handleErrorResponse(e);
                }
                isSubmitting.value = false;
            };

            const handleFileDeleted = async (fileName) => {
                try {
                    isFileDeleting.value = true;
                    const deleteUrl = `${deleteUploadedContentUrl.value}${fileName}/delete`;
                    await http.delete(deleteUrl);
                    message.success('Your file has been deleted.');
                    files.deleted.push(fileName);
                    updateFileList();
                } catch (e) {
                    message.error('There was an error while trying to delete your file, please try again later.');
                }
                isFileDeleting.value = false;
            };

            const handleErrorResponse = (response) => {
                const defaultMessage = 'There was an error while trying to submit your content, please try again later.';

                if (response.status === 423 || response.status === 409) {
                    message.error('Content has already been delivered.');
                } else if (response.status === 422) {
                    message.error(response?.body?.message || defaultMessage);
                } else {
                    message.error(defaultMessage);
                }

                emit('deliver-failed');
            };

            watch(deliverUploadedContent, () => {
                if (deliverUploadedContent.value) {
                    handleSubmit();
                }
            });

            watch(isReady, () => {
                emit('is-ready', isReady.value);
            });

            return {
                FAQ_UPLOADED_CONTENT_DELIVERY,
                url,
                postedAt,
                files,
                metrics,
                views,
                dateEnabled,
                isContentManualStatsEnabled,
                isMeta,
                isTiktok,
                isPinterest,
                isSubmitting,
                isFileDeleting,
                isReady,
                hasVideoFile,
                handleBeforeUpload,
                handleFileChange,
                handleFileDeleted,
                handleSubmit
            };
        }
    };
</script>

<style lang="scss" scoped>
    .margin-bottom-small {
        margin-bottom: $g-spacing-3;
    }

    .margin-bottom-normal {
        margin-bottom: $g-spacing-5;
    }

    .date-picker {
        width: 100%;
    }

    ::v-deep .ant-form-item-label {
        font-weight: $g-font-weight-normal !important;
    }

    ::v-deep .ant-upload.ant-upload-select {
        display: block;
        margin-bottom: $g-spacing-5;
    }

    @media (max-width: $g-screen-size-xs) {
        .full-width-mobile {
            width: 100%;
        }
    }
</style>
